import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ClipboardList,
  Keyboard,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Receipt,
  ShoppingCart,
  Wallet,
  X,
} from 'lucide-react';
import { AtalhosModal } from '../shared/components/AtalhosModal';
import { cn } from '../shared/lib/cn';
import { useAuthStore } from '../store/authStore';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/pdv', label: 'PDV', icon: ShoppingCart },
  { to: '/vendas', label: 'Vendas', icon: Receipt },
  { to: '/comandas', label: 'Comandas', icon: ClipboardList },
  { to: '/caixa', label: 'Caixa', icon: Wallet },
  { to: '/produtos', label: 'Produtos', icon: Package },
  { to: '/relatorios', label: 'Relatórios', icon: BarChart3 },
];

const GOTO_MAP: Record<string, string> = {
  d: '/dashboard',
  p: '/pdv',
  v: '/vendas',
  c: '/comandas',
  x: '/caixa',
  u: '/produtos',
  r: '/relatorios',
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (target.isContentEditable) return true;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [atalhosOpen, setAtalhosOpen] = useState(false);
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const gotoPending = useRef(false);
  const gotoTimer = useRef<number | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === '?') {
        e.preventDefault();
        setAtalhosOpen(true);
        return;
      }

      if (!gotoPending.current && e.key.toLowerCase() === 'g') {
        gotoPending.current = true;
        if (gotoTimer.current) window.clearTimeout(gotoTimer.current);
        gotoTimer.current = window.setTimeout(() => {
          gotoPending.current = false;
        }, 900);
        return;
      }

      if (gotoPending.current) {
        const target = GOTO_MAP[e.key.toLowerCase()];
        gotoPending.current = false;
        if (gotoTimer.current) window.clearTimeout(gotoTimer.current);
        if (target) {
          e.preventDefault();
          navigate(target);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const sidebar = (
    <nav className="flex h-full flex-col gap-1 p-3">
      <div className="mb-4 flex items-center gap-2 px-2 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 font-bold text-white">
          F
        </div>
        <div>
          <div className="text-sm font-semibold leading-none text-slate-900">FastPDV</div>
          <div className="mt-1 text-xs text-slate-500">Ponto de venda</div>
        </div>
      </div>
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-50 text-brand-700'
                : 'text-slate-700 hover:bg-slate-100',
            )
          }
        >
          <item.icon className="h-5 w-5" />
          <span>{item.label}</span>
        </NavLink>
      ))}
      <div className="mt-auto border-t border-slate-200 pt-3">
        <div className="px-3 py-2 text-xs">
          <div className="font-medium text-slate-900">{user?.nome}</div>
          <div className="mt-0.5 text-slate-500 capitalize">{user?.role}</div>
        </div>
        <button
          type="button"
          onClick={() => {
            setAtalhosOpen(true);
            setMobileOpen(false);
          }}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
        >
          <Keyboard className="h-5 w-5" />
          Atalhos
          <kbd className="ml-auto rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
            ?
          </kbd>
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
        >
          <LogOut className="h-5 w-5" />
          Sair
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-slate-200 bg-white md:block">
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 md:hidden">
          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="text-sm font-semibold text-slate-900">FastPDV</div>
        </header>

        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <AtalhosModal open={atalhosOpen} onClose={() => setAtalhosOpen(false)} />
    </div>
  );
}
